import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { IProductDTO } from '../../models';
import { UserService } from './user.service';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/** Gehashte Kontaktdaten für bessere Match-Quality bei Meta (Server hasht/normalisiert). */
export interface MetaUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

/** Produktbezug für ViewContent (PDP-Aufruf). */
export interface MetaContent {
  /** Shop-Artikel-ID (matcht den Meta-Katalog). */
  id: string;
  name: string;
  /** Monetärer Wert (netto), z. B. Kaufpreis. */
  value?: number;
  currency?: string;
}

/**
 * Dünner Wrapper um den Meta-Pixel (`fbq`), der über das rohe Snippet in
 * index.html global geladen wird. Alle Aufrufe sind SSR-sicher (No-op auf dem
 * Server) und feuern nur, wenn `fbq` im Browser tatsächlich verfügbar ist.
 * PageView kommt weiterhin aus index.html; hier ergänzen wir die Shop-Events.
 */
@Injectable({ providedIn: 'root' })
export class MetaTrackingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly user = inject(UserService);

  /**
   * Feuert ein Meta-Standard-Event, sofern der Pixel im Browser geladen ist.
   * Optionale `eventId` wird als `eventID` mitgegeben, damit Meta dieses
   * Browser-Ereignis mit dem serverseitigen CAPI-Ereignis (gleiche event_id +
   * event_name) dedupliziert.
   */
  track(event: string, params?: Record<string, unknown>, eventId?: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const fbq = window.fbq;
    if (typeof fbq !== 'function') return;
    if (eventId) fbq('track', event, params ?? {}, { eventID: eventId });
    else if (params) fbq('track', event, params);
    else fbq('track', event);
  }

  /**
   * Erzeugt eine eindeutige Ereignis-ID für die Browser-/Server-Deduplizierung.
   * Dieselbe ID muss an den Pixel und an den Server (Request-Body) gehen.
   */
  newEventId(): string {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
    return `ev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Aktuelle Seiten-URL für das Server-CAPI-Event (`event_source_url`).
   * Der Server kennt sonst nur den Tracking-Endpunkt, nicht die reale Seite.
   */
  currentPageUrl(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    return window.location.href;
  }

  /**
   * Übergibt bekannte Kontaktdaten (falls eingeloggt) ans Advanced Matching
   * des Pixels, bevor ein Event feuert. Gilt laut Meta für alle nachfolgenden
   * Events auf der Seite, verbessert also auch spätere Events desselben
   * Seitenaufrufs (z. B. AddToCart nach ViewContent).
   */
  private syncAdvancedMatching(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const fbq = window.fbq;
    if (typeof fbq !== 'function') return;
    const email = this.user.user()?.email;
    if (!email) return;
    fbq('set', 'userData', { em: email });
  }

  /** PDP-Aufruf → ViewContent. */
  viewContent(content: MetaContent): void {
    this.syncAdvancedMatching();
    this.track('ViewContent', {
      content_type: 'product',
      content_ids: [content.id],
      content_name: this.cleanName(content.name),
      value: content.value,
      currency: content.currency ?? 'EUR',
    });
  }

  /** Produkt in den Warenkorb → AddToCart. */
  addToCart(product: IProductDTO): void {
    this.track('AddToCart', {
      content_type: 'product',
      content_ids: [product.id],
      content_name: this.cleanName(product.displayName || product.title),
      value: product.price?.netPrice,
      currency: 'EUR',
    });
  }

  /** Entfernt HTML-Markup (z. B. das Trademark-<span>) und normalisiert Leerraum. */
  private cleanName(name: string): string {
    return (name || '')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
